<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute, useRouter } from 'vue-router';
import { useAuth } from './stores/auth';
import { useI18n } from 'vue-i18n';
import NotificationCenter from './components/NotificationCenter.vue';
import type { Language } from './types/api';

const auth = useAuth();
const router = useRouter();
const route = useRoute();
const { locale, t } = useI18n();
const logoUrl = import.meta.env.BASE_URL + 'Logo.svg';
const languageLabel = computed(() => locale.value === 'es' ? 'ES' : 'PT');
const { mobile } = useDisplay();
const notificationsOpen = ref(false);
const unreadNotifications = ref(0);
const languageSaving = ref(false);
const spaceRoute = computed(() => auth.area === 'admin' ? '/admin' : `/${auth.area}`);
const brandRoute = computed(() => auth.user && auth.area ? spaceRoute.value : '/');
const isCatalogView = computed(() => ['/catalogo', '/productos', '/fabricantes'].some(path => route.path === path || route.path.startsWith(`${path}/`)));
const isSpaceView = computed(() => Boolean(auth.user) && (
  route.path === spaceRoute.value || route.path.startsWith(`${spaceRoute.value}/`) ||
  ['/empresa', '/cuenta', '/oportunidades'].some(path => route.path === path || route.path.startsWith(`${path}/`))
));
const isAccountView = computed(() => route.path === '/cuenta' || route.path.startsWith('/cuenta/'));

watch(() => route.fullPath, () => { void nextTick(() => document.getElementById('main-content')?.focus()); });
watch(
  () => auth.user && 'preferredLanguage' in auth.user ? auth.user.preferredLanguage : undefined,
  preferredLanguage => {
    if (!preferredLanguage) return;
    locale.value = preferredLanguage === 'PT_BR' ? 'pt-BR' : 'es';
    localStorage.setItem('bralink_locale', locale.value);
  },
  { immediate: true }
);
watch(
  () => auth.company?.type === 'EXPORTER' || (!auth.user && route.path === '/registro/exportador'),
  isExporterFlow => {
    if (auth.user) return;
    if (localStorage.getItem('bralink_locale_selected') === 'true') return;
    locale.value = isExporterFlow ? 'pt-BR' : 'es';
  },
  { immediate: true }
);

async function toggleLanguage() {
  if (languageSaving.value) return;
  const previous = locale.value;
  const previousSelection = localStorage.getItem('bralink_locale_selected');
  const next = previous === 'es' ? 'pt-BR' : 'es';
  locale.value = next;
  localStorage.setItem('bralink_locale', next);
  localStorage.setItem('bralink_locale_selected', 'true');

  if (auth.user && 'preferredLanguage' in auth.user) {
    languageSaving.value = true;
    try {
      await auth.setPreferredLanguage((next === 'pt-BR' ? 'PT_BR' : 'ES') as Language);
    } catch {
      locale.value = previous;
      localStorage.setItem('bralink_locale', previous);
      if (previousSelection === 'true') localStorage.setItem('bralink_locale_selected', 'true');
      else localStorage.removeItem('bralink_locale_selected');
    } finally {
      languageSaving.value = false;
    }
  }
}

function logout() {
  unreadNotifications.value = 0;
  auth.logout();
  void router.push('/');
}

function openNotifications() {
  notificationsOpen.value = true;
}
</script>

<template>
  <v-app>
    <a class="skip-link" href="#main-content">{{ t('common.skipToContent') }}</a>
    <v-app-bar class="site-bar" color="white" elevation="0">
      <v-container class="d-flex align-center header-content">
        <router-link class="brand" :to="brandRoute"><img :src="logoUrl" alt="BraLink"/></router-link>
        <v-spacer/>
        <div class="mobile-header-actions">
          <v-btn class="language-button" variant="text" :disabled="languageSaving" :aria-label="languageLabel === 'ES' ? t('common.languageToPortuguese') : t('common.languageToSpanish')" @click="toggleLanguage"><v-icon start size="18">mdi-translate</v-icon>{{ languageLabel }}</v-btn>
        </div>
        <nav class="header-nav" :aria-label="t('common.mainNavigation')">
          <v-btn variant="text" to="/catalogo" :class="{ 'nav-active': isCatalogView }" :aria-current="isCatalogView ? 'page' : undefined">{{ $t('nav.catalog') }}</v-btn>
          <v-btn v-if="auth.user" class="header-cta" color="primary" variant="flat" :to="spaceRoute" :class="{ 'nav-active': isSpaceView }" :aria-current="isSpaceView ? 'page' : undefined">{{ $t('general.space') }}</v-btn>
          <v-btn v-if="!auth.user" variant="text" to="/login" :class="{ 'nav-active': route.path === '/login' }">{{ $t('nav.login') }}</v-btn>
          <v-btn v-if="!auth.user" class="header-cta" color="primary" variant="flat" to="/registro/comprador" :class="{ 'nav-active': route.path.startsWith('/registro') }">{{ $t('nav.register') }}</v-btn>
          <v-btn class="language-button" variant="text" :disabled="languageSaving" :aria-label="languageLabel === 'ES' ? t('common.languageToPortuguese') : t('common.languageToSpanish')" @click="toggleLanguage"><v-icon start size="18">mdi-translate</v-icon>{{ languageLabel }}</v-btn>
          <v-menu v-if="auth.user" location="bottom end">
            <template #activator="{ props: menuProps }">
              <v-btn v-bind="menuProps" icon variant="text" class="account-menu-button" :aria-label="t('account.profileMenu')"><v-icon>mdi-account-circle-outline</v-icon><span v-if="unreadNotifications" class="account-unread-dot" aria-hidden="true"></span></v-btn>
            </template>
            <v-list class="account-menu-list" min-width="248">
              <div class="account-menu-heading"><strong>{{ auth.user.name }}</strong><span>{{ auth.user.email }}</span></div>
              <v-divider />
              <v-list-item prepend-icon="mdi-account-circle-outline" :title="t('account.profile')" :to="{ path: '/cuenta', query: { section: 'profile' } }" />
              <v-list-item prepend-icon="mdi-bell-outline" :title="t('account.notifications')" @click="openNotifications"><template v-if="unreadNotifications" #append><span class="account-unread-count">{{ unreadNotifications > 9 ? '9+' : unreadNotifications }}</span></template></v-list-item>
              <v-divider />
              <v-list-item prepend-icon="mdi-logout" :title="$t('nav.logout')" @click="logout" />
            </v-list>
          </v-menu>
        </nav>
      </v-container>
    </v-app-bar>
    <v-main id="main-content" class="site-main" tabindex="-1"><router-view/></v-main>
    <NotificationCenter v-if="auth.user" v-model="notificationsOpen" @unread-change="unreadNotifications=$event"/>
    <v-footer v-if="!['/login','/catalogo'].includes(route.path) && !route.path.startsWith('/registro')" class="site-footer justify-center">{{ $t('general.brand') }}</v-footer>
    <nav v-if="mobile" class="mobile-bottom-nav" :aria-label="t('common.mainNavigation')">
      <router-link class="mobile-nav-item" :class="{ 'is-active': isCatalogView }" to="/catalogo" :aria-current="isCatalogView ? 'page' : undefined">
        <v-icon aria-hidden="true">mdi-view-grid-outline</v-icon><span>{{ $t('nav.catalog') }}</span>
      </router-link>
      <router-link class="mobile-nav-item" :class="{ 'is-active': isSpaceView && !isAccountView }" :to="auth.user ? spaceRoute : '/login'" :aria-current="isSpaceView && !isAccountView ? 'page' : undefined">
        <v-icon aria-hidden="true">mdi-briefcase-outline</v-icon><span>{{ $t('general.space') }}</span>
      </router-link>
      <v-menu v-if="auth.user" location="top center" offset="8">
        <template #activator="{ props: menuProps }">
          <button v-bind="menuProps" type="button" class="mobile-nav-item mobile-nav-account" :class="{ 'is-active': isAccountView }" :aria-label="t('account.title')">
            <v-icon aria-hidden="true">mdi-account-circle-outline</v-icon><span>{{ t('account.title') }}</span>
            <span v-if="unreadNotifications" class="account-unread-dot" aria-hidden="true"></span>
          </button>
        </template>
        <v-list class="account-menu-list mobile-account-menu" min-width="220">
          <v-list-item prepend-icon="mdi-account-circle-outline" :title="t('account.profile')" :to="{ path: '/cuenta', query: { section: 'profile' } }" />
          <v-list-item prepend-icon="mdi-bell-outline" :title="t('account.notifications')" @click="openNotifications"><template v-if="unreadNotifications" #append><span class="account-unread-count">{{ unreadNotifications > 9 ? '9+' : unreadNotifications }}</span></template></v-list-item>
          <v-list-item prepend-icon="mdi-logout" :title="$t('nav.logout')" @click="logout" />
        </v-list>
      </v-menu>
      <router-link v-else class="mobile-nav-item" :class="{ 'is-active': route.path === '/login' }" to="/login" :aria-current="route.path === '/login' ? 'page' : undefined">
        <v-icon aria-hidden="true">mdi-account-circle-outline</v-icon><span>{{ t('account.title') }}</span>
      </router-link>
    </nav>
  </v-app>
</template>

<style>
.skip-link{position:fixed;z-index:3000;top:8px;left:8px;transform:translateY(-160%);padding:10px 14px;border-radius:6px;background:#fff;color:#10517f;font-weight:800;box-shadow:0 3px 12px rgb(0 0 0 / .2)}.skip-link:focus{transform:translateY(0)}
.v-application :focus-visible:not(input):not(textarea):not(.v-field__input){outline:3px solid #e5b300!important;outline-offset:3px}
.v-application input:focus-visible,.v-application textarea:focus-visible,.v-application .v-field__input:focus-visible{outline:none!important;outline-offset:0}
.v-application .v-btn{text-transform:none!important;letter-spacing:normal}
.v-application h1,.v-application h2,.v-application h3,.v-application .v-card-title{font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif!important}.catalog-content{margin-top:28px!important}.catalog-filters{box-shadow:none!important}.catalog-filters .v-field{background:#fff!important}.catalog-filters .v-field__overlay{opacity:0!important}.catalog-page .product-title,.catalog-page .product-title:visited,.catalog-page .company-name,.catalog-page .company-name:visited{color:#10517f!important;text-decoration:none!important}.catalog-page .product-title{display:block;line-height:1.25}.catalog-page .company-name{display:inline-block}.catalog-page .product-title:hover,.catalog-page .company-name:hover{color:#0a7744!important;text-decoration:none!important}.manufacturer-hero{padding:24px 0 28px!important;background:linear-gradient(120deg,#0e4a78,#105d8e)!important;color:#fff!important}.manufacturer-hero h1{font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif!important;color:#fff!important}.manufacturer-hero .hero-back{color:#dceaf2!important}.manufacturer-hero .hero-description{color:#e0ecf2!important}.manufacturer-hero .hero-location{color:#d1e3ed!important}.manufacturer-product-card{border:1px solid #dce5ea!important;border-radius:9px!important;background:#fff!important}.manufacturer-product-card .product-link,.manufacturer-product-card .product-link:visited{color:#10517f!important;text-decoration:none!important}
.dashboard-page{max-width:1120px!important}.company-editor-card .v-col-md-8{flex:0 0 50%!important;max-width:50%!important}
.v-application{font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif!important;color:#20313e}.site-bar{border-bottom:1px solid #e3e9ed!important}.header-content{min-height:72px}.brand{display:flex;align-items:center}.brand img{width:128px;height:32px;object-fit:contain}.header-nav{display:flex;align-items:center;gap:3px}.header-nav .v-btn{font-size:.72rem;font-weight:700;letter-spacing:.08em}.header-nav .v-btn.nav-active{border-radius:0!important;background:transparent!important;box-shadow:inset 0 -2px 0 #2fa84f!important;color:#10517f!important}.header-nav .v-btn.nav-active .v-btn__overlay{opacity:0!important}.header-cta{min-height:34px}.header-cta.nav-active{background:transparent!important;color:#10517f!important}.language-button{min-width:58px}.mobile-header-actions{display:none}.site-footer{height:58px!important;min-height:58px!important;padding:0!important;flex:0 0 58px!important;background:#083c64!important;color:#d8e5ed!important;font-size:.78rem;font-weight:600;letter-spacing:.04em}@media(prefers-reduced-motion:reduce){*,*::before,*::after{scroll-behavior:auto!important;transition-duration:.01ms!important;animation-duration:.01ms!important;animation-iteration-count:1!important}}@media(max-width:720px){.header-content{padding-inline:18px!important}.header-nav{display:none}.mobile-header-actions{display:flex;align-items:center;gap:2px}.mobile-header-actions .v-btn{min-height:44px;min-width:44px}.brand img{width:116px}}@media(max-width:700px){.manufacturer-hero{padding:20px 0 28px!important}}@media(max-width:600px){.v-overlay__content>.v-dialog{width:calc(100% - 32px)!important;max-height:calc(100dvh - 32px);margin:16px}.v-overlay__content>.v-dialog>.v-card{max-height:calc(100dvh - 32px)}.v-overlay__content>.v-dialog>.v-card>.v-card-text{overflow-y:auto}}
</style>

<style scoped>
.account-menu-button { position: relative; }
.account-unread-dot { position: absolute; top: 8px; right: 8px; width: 9px; height: 9px; border: 2px solid #fff; border-radius: 50%; background: #c62828; }
.account-menu-list { padding: 8px; }
.account-menu-heading { display: grid; gap: 3px; padding: 12px 16px; }
.account-menu-heading strong { color: #20313e; font-size: .9rem; }
.account-menu-heading span { color: #52616b; font-size: .78rem; }
.account-unread-count { display: inline-grid; min-width: 21px; height: 21px; place-items: center; padding: 0 5px; border-radius: 12px; background: #c62828; color: #fff; font-size: .72rem; font-weight: 800; }
</style>

<style>
@media (max-width: 959px) {
  .dashboard-page .company-editor-card .v-col-md-8 { flex: 0 0 100% !important; max-width: 100% !important; }
}
@media (max-width: 720px) {
  .site-main { padding-bottom: calc(72px + env(safe-area-inset-bottom)) !important; }
  .site-footer { margin-bottom: calc(64px + env(safe-area-inset-bottom)) !important; }
  .mobile-bottom-nav {
    position: fixed;
    z-index: 1004;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    height: calc(64px + env(safe-area-inset-bottom));
    padding: 3px 8px env(safe-area-inset-bottom);
    border-top: 1px solid #dce5ea;
    background: #fff;
    box-shadow: 0 -4px 16px rgb(23 48 62 / .08);
  }
  .mobile-nav-item {
    position: relative;
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    padding: 3px 4px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #52616b;
    font: inherit;
    text-decoration: none;
    cursor: pointer;
  }
  .mobile-nav-item .v-icon { height: 25px; font-size: 22px; }
  .mobile-nav-item span:not(.account-unread-dot) { overflow: hidden; max-width: 100%; font-size: .68rem; font-weight: 400; line-height: 1.15; text-overflow: ellipsis; white-space: nowrap; }
  .mobile-nav-item.is-active { color: #0e4a78; }
  .mobile-nav-item.is-active span:not(.account-unread-dot) { font-weight: 800; }
  .mobile-nav-account .account-unread-dot { top: 3px; right: calc(50% - 19px); }
  .mobile-account-menu { margin-bottom: 4px; }
}
</style>
